import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Printer, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COMPANY } from "@/lib/company";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  message: "",
  interest: "Gesamtlösung",
};

export default function Footer() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus({ state: "error", msg: "Bitte füllen Sie alle Pflichtfelder aus." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({
        state: "success",
        msg: "Vielen Dank – wir melden uns kurzfristig bei Ihnen.",
      });
      setForm(initial);
    } catch (err) {
      setStatus({
        state: "error",
        msg:
          err?.response?.data?.detail ||
          "Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
      });
    }
  };

  return (
    <footer
      id="kontakt"
      data-testid="footer-section"
      className="relative bg-[#0B1220] text-white"
    >
      <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <p className="tag-line text-xs font-mono-tech uppercase tracking-[0.25em] text-white/60 mb-6">
              Kontakt · Projektanfrage
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Sprechen wir über Ihr Projekt.
            </h2>
            <p className="mt-4 text-white/70 max-w-xl">
              Hinterlassen Sie uns eine Nachricht – ein technischer
              Ansprechpartner meldet sich werktags innerhalb von 24 Stunden.
            </p>

            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
            >
              <div className="sm:col-span-1">
                <Label htmlFor="name" className="text-white/80 text-xs uppercase tracking-widest">
                  Name *
                </Label>
                <Input
                  id="name"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={update("name")}
                  required
                  className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#5BA8FF]"
                  placeholder="Max Mustermann"
                />
              </div>
              <div className="sm:col-span-1">
                <Label htmlFor="company" className="text-white/80 text-xs uppercase tracking-widest">
                  Firma
                </Label>
                <Input
                  id="company"
                  data-testid="contact-input-company"
                  value={form.company}
                  onChange={update("company")}
                  className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#5BA8FF]"
                  placeholder="Ihr Unternehmen"
                />
              </div>
              <div className="sm:col-span-1">
                <Label htmlFor="email" className="text-white/80 text-xs uppercase tracking-widest">
                  E-Mail *
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={update("email")}
                  required
                  className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#5BA8FF]"
                  placeholder="name@firma.de"
                />
              </div>
              <div className="sm:col-span-1">
                <Label htmlFor="phone" className="text-white/80 text-xs uppercase tracking-widest">
                  Telefon
                </Label>
                <Input
                  id="phone"
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#5BA8FF]"
                  placeholder="+49 ..."
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-white/80 text-xs uppercase tracking-widest">
                  Interesse
                </Label>
                <Select value={form.interest} onValueChange={update("interest")}>
                  <SelectTrigger
                    data-testid="contact-select-interest"
                    className="mt-2 bg-white/5 border-white/15 text-white focus:ring-[#5BA8FF]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B1220] text-white border-white/15">
                    <SelectItem value="Gesamtlösung">Gesamtlösung</SelectItem>
                    <SelectItem value="Videoüberwachung">Videoüberwachung</SelectItem>
                    <SelectItem value="Einbruchmeldeanlagen">Einbruchmeldeanlagen</SelectItem>
                    <SelectItem value="Kameramast">Xortec Kameramast</SelectItem>
                    <SelectItem value="Anschlusskasten">
                      Xortec Kameraanschlusskasten
                    </SelectItem>
                    <SelectItem value="Netzwerkinfrastruktur">
                      Netzwerkinfrastruktur
                    </SelectItem>
                    <SelectItem value="Beratung">Technische Beratung</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="message" className="text-white/80 text-xs uppercase tracking-widest">
                  Ihre Nachricht *
                </Label>
                <Textarea
                  id="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  required
                  rows={5}
                  className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#5BA8FF]"
                  placeholder="Erzählen Sie uns kurz von Ihrem Projekt..."
                />
              </div>

              <div className="sm:col-span-2 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={status.state === "loading"}
                  className="inline-flex items-center justify-center gap-2 bg-[#0055A4] hover:bg-[#003e7a] text-white px-6 py-3.5 rounded-md font-medium transition-colors disabled:opacity-60"
                >
                  {status.state === "loading" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    "Anfrage senden"
                  )}
                </button>

                {status.state === "success" && (
                  <div
                    data-testid="contact-success"
                    className="flex items-center gap-2 text-sm text-emerald-300"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    {status.msg}
                  </div>
                )}
                {status.state === "error" && (
                  <div
                    data-testid="contact-error"
                    className="flex items-center gap-2 text-sm text-amber-300"
                  >
                    <AlertCircle className="h-4 w-4" />
                    {status.msg}
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Contact info */}
          <div className="lg:col-span-5 lg:pl-10 lg:border-l lg:border-white/10">
            <p className="tag-line text-xs font-mono-tech uppercase tracking-[0.25em] text-white/60 mb-6">
              Technischer Ansprechpartner
            </p>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-white/5 border border-white/10 text-[#5BA8FF]">
                  <Phone className="h-4 w-4" />
                </span>
                <div>
                  <div className="font-mono-tech text-xs uppercase tracking-widest text-white/50">
                    Telefon
                  </div>
                  <a
                    href={`tel:${COMPANY.phoneHref}`}
                    className="font-display text-lg text-white hover:text-[#5BA8FF] transition-colors"
                  >
                    {COMPANY.phone}
                  </a>
                  <div className="text-xs text-white/50 mt-1 flex items-center gap-2">
                    <Printer className="h-3 w-3" /> Fax {COMPANY.fax}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-white/5 border border-white/10 text-[#5BA8FF]">
                  <MapPin className="h-4 w-4" />
                </span>
                <div>
                  <div className="font-mono-tech text-xs uppercase tracking-widest text-white/50">
                    Standort
                  </div>
                  <div className="font-display text-lg text-white">
                    {COMPANY.name}
                  </div>
                  <div className="text-sm text-white/60">
                    {COMPANY.street}
                    <br />
                    {COMPANY.zip} {COMPANY.city} · {COMPANY.country}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-white/10">
              <p className="font-mono-tech text-xs uppercase tracking-widest text-white/50 mb-3">
                Geschäftszeiten
              </p>
              <p className="text-sm text-white/70 leading-relaxed">
                Mo – Fr · 8:00 – 17:00 Uhr
                <br />
                Außerhalb der Geschäftszeiten erreichen Sie uns per E-Mail.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between text-xs text-white/50">
          <div className="flex items-center gap-3">
            <img
              src="/brand/xortec-logo-light.png"
              alt="Xortec"
              className="h-6 w-auto"
            />
            <span>© {new Date().getFullYear()} {COMPANY.name} · Alle Rechte vorbehalten.</span>
          </div>
          <div className="flex gap-6">
            <Link to="/impressum" data-testid="footer-link-impressum" className="hover:text-white">Impressum</Link>
            <Link to="/datenschutz" data-testid="footer-link-datenschutz" className="hover:text-white">Datenschutz</Link>
            <a href={`https://${COMPANY.website}`} target="_blank" rel="noreferrer" className="hover:text-white">{COMPANY.website}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
